import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Typography, Textarea } from "@castlery/fortress";
import { TaskAlt } from "@castlery/fortress/Icons";
import { Star, StarFilled } from "@/components/icons";
import Drawer from "@/components/ui/drawer";
import { useToast } from "@/hooks/useToast";
import { useChatStore } from "@/stores/chatStore";
import { generateArray } from "@/utils/array";
import { apiPost } from "@/utils/apiHelper";
import { CSAT_API } from "@/config/api";
import { throwCasaApiError } from "@/utils";
import { EMPTY_CONVERSATION_ID, FeedbackSentence } from "@/constant";
import { Conversation } from "@/types";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  trackEvent,
} from "@/utils/analytics";

interface CSATFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (csat: {
    conversationId: number;
    rate: string;
    rateReason?: string;
    advice?: string;
  }) => void;
  onCancel: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  conversation: Conversation | null;
}

const lowScoreQuickReplies = [
  "Slow Response",
  "Robotic Reply",
  "Insufficient Info",
  "No Empathy",
  "Irrelevant Recommendation",
  "Generic Advice",
];

const highScoreQuickReplies = [
  "Fast Response",
  "Clear Info",
  "Accurate Recommendation",
  "User-friendly",
  "Personalized Advice",
  "Time-saving",
];

export const CSAT: React.FC<CSATFormProps> = ({
  onSubmit,
  onCancel,
  open,
  onOpenChange,
  containerRef,
  conversation,
}) => {
  const { toast } = useToast();
  const {
    abVariant,
    hadHandledProactiveGreeting: proactiveTriggered,
    entryPath,
  } = useChatStore();
  const [rate, setRate] = useState(0);
  const [rateReason, setRateReason] = useState("");
  const [advice, setAdvice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0); // 新增：跟踪hover状态
  const [labels, setLabels] = useState<string[]>([]); // 动态标签

  useEffect(() => {
    if (rate >= 4) {
      setLabels(highScoreQuickReplies);
    } else {
      setLabels(lowScoreQuickReplies);
    }
  }, [rate]);

  // 处理表单字段显示动画
  useEffect(() => {
    if (rate !== 0) {
      // 选中评分后延迟显示表单字段
      const timer = setTimeout(() => {
        setShowCommentForm(true);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setShowCommentForm(false);
    }
  }, [rate]);

  const handleSubmit = async () => {
    setSubmitting(true);

    const SUBMISSION_FAILED_MESSAGE =
      "Submission failed, please try again later. Thank you for your patience.";

    try {
      // 准备 CSAT 数据
      const csatData = {
        conversation_id: conversation?.conversation_id || EMPTY_CONVERSATION_ID,
        rate: rate.toString(),
        rate_reason: rateReason.trim() || undefined,
        advice: advice.trim() || undefined,
      };

      // 提交 CSAT 数据到后端
      const response = await apiPost(CSAT_API.submit(), csatData);

      if (response.code !== 0) {
        throw new Error(SUBMISSION_FAILED_MESSAGE);
      }

      // 执行成功回调
      onSubmit({
        conversationId: conversation?.conversation_id || EMPTY_CONVERSATION_ID,
        rate: rate.toString(),
        rateReason,
        advice,
      });

      setSubmitted(true);
    } catch (error) {
      console.error("Failed to submit CSAT:", error);

      onSubmit({
        conversationId: conversation?.conversation_id || EMPTY_CONVERSATION_ID,
        rate: rate.toString(),
        rateReason,
        advice,
      });

      toast({
        description:
          error instanceof Error ? error.message : SUBMISSION_FAILED_MESSAGE,
      });

      throwCasaApiError(error, "[CSAT] Failed to submit CSAT");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStarClick = (rating: number) => {
    setRate(rating);
  };

  // 处理hover事件
  const handleMouseEnter = (rating: number) => {
    setHoveredRating(rating);
  };

  const handleMouseLeave = () => {
    setHoveredRating(0);
  };

  // 判断星星是否应该高亮
  const isStarActive = (starIndex: number) => {
    const currentRating = hoveredRating || rate;
    return starIndex < currentRating;
  };

  const handleClose = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "csat",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "close",
    });

    onOpenChange(false);
    onCancel();
    // 重置状态
    setRate(0);
    setHoveredRating(0);
    setRateReason("");
    setAdvice("");
    setShowCommentForm(false);
    setSubmitted(false);
  };

  return (
    <Drawer
      className="z-[1001] sm:z-10"
      open={open}
      onClose={() => handleClose()}
      direction="bottom"
      showCloseButton={true}
      container={containerRef?.current}
    >
      <div className="max-h-[80vh] bg-[#FBF9F4]">
        {/* Drawer Title */}
        <div className="py-5 px-6 text-left">
          <div className="flex flex-col justify-between">
            <div className="flex items-center justify-between mb-[20px] text-[20px] text-foreground leading-[1.2]">
              <span>{!submitted ? "How is AI Casa service?" : ""}</span>
            </div>
            {!submitted && (
              <Typography level="subh2" className="text-primary">
                PLEASE RATE YOUR EXPERIENCE
              </Typography>
            )}
          </div>
        </div>

        {/* Drawer Content */}
        <div>
          {!submitted ? (
            <div className="flex-1 flex flex-col">
              <div className="px-6 flex-1 flex flex-col">
                {/* 星级评分区域 */}
                <div className="text-center mb-5">
                  <div className="flex justify-center">
                    {generateArray({
                      length: 5,
                      start: 1,
                    }).map((rating, index) => (
                      <motion.div
                        key={rating}
                        className="cursor-pointer"
                        onClick={() => handleStarClick(rating)}
                        onMouseEnter={() => handleMouseEnter(rating)}
                        onMouseLeave={handleMouseLeave}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                          rotate: 0,
                        }}
                        transition={{
                          delay: index * 0.1,
                          type: "spring",
                          stiffness: 200,
                          damping: 15,
                        }}
                        whileHover={{
                          scale: 1.1,
                          rotate: [0, -10, 10, 0],
                          transition: { duration: 0.3 },
                        }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <motion.div
                          animate={{
                            color: "#844025",
                            scale: isStarActive(index) ? 1.1 : 1,
                          }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          {isStarActive(index) ? (
                            <StarFilled className="w-9 h-9 fill-current" />
                          ) : (
                            <Star className="w-9 h-9 fill-current" />
                          )}
                        </motion.div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* 表单字段区域 - 选中评分后滑入显示 */}
                <div>
                  {!showCommentForm && <div className="h-16" />}
                  {showCommentForm && (
                    <div className="overflow-hidden">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[16px] leading-[1.2] text-foreground mb-2">
                            What's the main reason for your score?
                          </label>
                          <div className="flex flex-row flex-wrap gap-2">
                            {labels.map((label) => (
                              <div
                                key={label}
                                className="flex-shrink-0 py-[6px] px-[12px] border border-[#BEBEBE] cursor-pointer"
                                onClick={() =>
                                  // append rate reason
                                  setRateReason((prev) =>
                                    prev ? prev + ", " + label : label,
                                  )
                                }
                              >
                                <Typography
                                  level="caption1"
                                  className="text-[#212121]"
                                >
                                  {label}
                                </Typography>
                              </div>
                            ))}
                          </div>
                          <Textarea
                            placeholder="Please specify..."
                            value={rateReason}
                            onChange={(e) => setRateReason(e.target.value)}
                            endDecorator={
                              <Typography
                                level="body2"
                                sx={{
                                  ml: "auto",
                                }}
                              >
                                {rateReason.length}/500
                              </Typography>
                            }
                            sx={{ minWidth: 300 }}
                          />
                        </div>

                        <div>
                          <label className="block text-[16px] leading-[1.2] text-foreground mb-2">
                            Is there anything you'd like to add?
                          </label>

                          <Textarea
                            placeholder="Please specify..."
                            value={advice}
                            onChange={(e) => setAdvice(e.target.value)}
                            endDecorator={
                              <Typography
                                level="body2"
                                sx={{
                                  ml: "auto",
                                }}
                              >
                                {advice.length}/500
                              </Typography>
                            }
                            sx={{ minWidth: 300 }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* 感谢信息 - 替换整个表单内容 */
            <div
              key="thank-you-content"
              className="flex flex-col items-center justify-center text-center"
            >
              <div className="mb-3">
                <span>
                  <TaskAlt
                    sx={(theme) => ({
                      width: theme.spacing(16),
                      height: theme.spacing(16),
                      fill: theme.palette.brand.leafGreen[500],
                    })}
                  />
                </span>
              </div>

              <Typography level="body1">{FeedbackSentence}</Typography>
            </div>
          )}
        </div>

        {/* Drawer Footer */}

        {!submitted && showCommentForm && (
          <div className="p-6">
            <Button
              className="w-full disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              onClick={handleSubmit}
              disabled={submitting}
            >
              SUBMIT
            </Button>
          </div>
        )}
        {submitted && (
          <div className="p-6">
            <Button
              className="w-full transition-all duration-300"
              size="lg"
              onClick={handleClose}
            >
              CLOSE
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
};
